import type { AvatarList } from './type'
import { getStaticAssetUrl } from '../../file/aws-static-url'

export interface GenerateAvatarListOptions {
  /**
   * Number of avatars to generate
   * @default 12
   */
  count?: number
  /**
   * Path prefix for avatar assets (e.g., 'avatar/', 'avatar/child/')
   * In the future, different entity types can have different paths
   * @default 'avatar/'
   */
  pathPrefix?: string
  /**
   * Alt text prefix for accessibility
   * @default 'Avatar'
   */
  altPrefix?: string
  /**
   * Whether to include legacy icon IDs for backward compatibility
   * @default false
   */
  includeLegacyIds?: boolean
}

/**
 * Dynamically generates an avatar list based on configuration.
 * This centralizes avatar list generation to reduce duplication and make it easier to maintain.
 *
 * @param options - Configuration options for avatar generation
 * @returns Array of avatar list items
 *
 * @example
 * // Generate default 12 avatars
 * const avatars = generateAvatarList()
 *
 * @example
 * // Generate with custom path (when assets are ready)
 * const childAvatars = generateAvatarList({ pathPrefix: 'avatar/child/' })
 *
 * @example
 * // Generate with legacy IDs for backward compatibility
 * const mainAvatars = generateAvatarList({ includeLegacyIds: true })
 */
export function generateAvatarList(options: GenerateAvatarListOptions = {}): AvatarList[] {
  const { count = 12, pathPrefix = 'avatar/', altPrefix = 'Avatar', includeLegacyIds = false } = options

  return Array.from({ length: count }, (_, index) => {
    const avatarNumber = index + 1
    const fileName = `${avatarNumber}.png`
    const assetPath = `${pathPrefix}${fileName}` as const

    const avatar: AvatarList = {
      src: getStaticAssetUrl(assetPath),
      alt: `${altPrefix} ${avatarNumber}`,
      key: fileName as `${string}.${string}`,
    }

    if (includeLegacyIds) {
      avatar.legacyIconId = avatarNumber
    }

    return avatar
  })
}
